import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormNode } from '@/utils/api/aboboApiTypes';
import BaseQuestionType from './BaseQuestionType';

// Import shadcn components
import { Textarea } from '@/components/ui';
import QuestionHeading from '@/components/QuestionHeading';
import QuestionHelperText from '@/components/QuestionHelperText';

interface OpenEndedProps {
  node: FormNode;
}

export default forwardRef<any, OpenEndedProps>(function OpenEnded(
  { node },
  ref
) {
  const { t } = useTranslation();

  const validateOpenEnded = (values: any[]) => {
    // Always expect array format, get the first item for open-ended questions
    const value = values?.[0]?.data || '';
    
    if (node.metadata?.required && (!value || !value.trim())) {
      return t('questionTypes.openEnded.validation.required');
    }
    
    // Add any additional validation here
    if (value.length > 1000) {
      return t('questionTypes.openEnded.validation.tooLong');
    }
    
    return true;
  };

  const transformInput = (values: any[]) => {
    return values?.[0]?.data || '';
  };

  const transformOutput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    return value ? [{ data: value }] : [];
  };

  return (
    <BaseQuestionType
      node={node}
      validate={validateOpenEnded}
      transform={{
        input: transformInput,
        output: transformOutput,
      }}
    >
      {(field) => (
        <div className="space-y-4" ref={ref}>
          <QuestionHeading required={node.metadata.required}>{node.body}</QuestionHeading>

          {node.helperText && (
            <QuestionHelperText>
              {node.helperText}
            </QuestionHelperText>
          )}

          <Textarea
            {...field}
            autoComplete="off"
            placeholder={t('questionTypes.openEnded.placeholder')}
            rows={node.nodeSubtype === 'open_ended_long' ? 4 : 3}
            className="w-full resize-y"
          />

          {field.error && (
            <p className="text-red-500 text-sm text-center">{field.error}</p>
          )}
        </div>
      )}
    </BaseQuestionType>
  );
});
